import React, { useState, useSyncExternalStore } from 'react';
import { Box, Button, IconButton, Snackbar, SnackbarContent } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloseIcon from '@mui/icons-material/Close';
import { applyUpdate } from '../../serviceWorkerRegistration';
import { BOTTOM_NAV_HEIGHT, useBottomNavVisible } from '../Layout/useBottomNavVisible';
import { swUpdateStore } from './swUpdateStore';
import { useOnlineStatus } from './useOnlineStatus';

// Az App MUI témájának zöldjei (a PwaStatus a ThemeProvider-en kívül renderel)
const GREEN_DARK = '#2e7d32';
const GREEN_LIGHT = '#a5d6a7';

/** Kis, a tartalmat lejjebb toló sáv a lap tetején, amíg nincs hálózat. */
export const OfflineBanner: React.FC = () => {
  const online = useOnlineStatus();
  if (online) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1250,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 2,
        py: 0.5,
        minHeight: 32,
        bgcolor: '#5d4037',
        color: '#fff',
        fontSize: '0.875rem',
        lineHeight: 1.3,
        textAlign: 'center',
      }}
    >
      <CloudOffIcon fontSize="small" aria-hidden />
      <span>Nincs internetkapcsolat — a módosítások nem mentődnek</span>
    </Box>
  );
};

/** Nem tolakodó értesítés új verzióról, „Frissítés” gombbal. */
export const UpdateAvailableNotice: React.FC = () => {
  const registration = useSyncExternalStore(swUpdateStore.subscribe, swUpdateStore.getSnapshot, () => null);
  const [dismissedFor, setDismissedFor] = useState<ServiceWorkerRegistration | null>(null);
  const [updating, setUpdating] = useState(false);
  const bottomNavVisible = useBottomNavVisible();

  const open = !!registration && dismissedFor !== registration;

  const handleUpdate = () => {
    if (!registration) return;
    setUpdating(true);
    applyUpdate(registration);
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      sx={
        bottomNavVisible
          ? { bottom: `calc(${BOTTOM_NAV_HEIGHT + 8}px + env(safe-area-inset-bottom)) !important` }
          : undefined
      }
    >
      <SnackbarContent
        data-testid="update-available"
        sx={{ bgcolor: GREEN_DARK, color: '#fff' }}
        message="Új verzió érhető el"
        action={
          <>
            <Button
              size="small"
              onClick={handleUpdate}
              disabled={updating}
              sx={{ color: GREEN_LIGHT, fontWeight: 700 }}
            >
              Frissítés
            </Button>
            <IconButton
              size="small"
              aria-label="Később"
              onClick={() => setDismissedFor(registration)}
              sx={{ color: '#fff' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      />
    </Snackbar>
  );
};

/** PWA állapotjelzők: offline sáv + frissítés-értesítés. */
const PwaStatus: React.FC = () => (
  <>
    <OfflineBanner />
    <UpdateAvailableNotice />
  </>
);

export default PwaStatus;
